import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';

export type ReportIntervalOption =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'this_month'
  | 'custom_month'
  | 'custom_year';

export function useReportDateRange(initialInterval: ReportIntervalOption = 'this_month') {
  const [selectedInterval, setSelectedInterval] = useState<ReportIntervalOption>(initialInterval);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();

  const resolveRange = useCallback(
    (interval: ReportIntervalOption, month: number, year: number) => {
      const now = new Date();
      switch (interval) {
        case 'today':
          return { start: startOfDay(now), end: endOfDay(now) };
        case 'yesterday': {
          const y = subDays(now, 1);
          return { start: startOfDay(y), end: endOfDay(y) };
        }
        case 'this_week':
          return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
        case 'this_month':
          return { start: startOfMonth(now), end: endOfMonth(now) };
        case 'custom_month':
          return {
            start: startOfMonth(new Date(year, month, 1)),
            end: endOfMonth(new Date(year, month, 1)),
          };
        case 'custom_year':
          return {
            start: startOfMonth(new Date(year, 0, 1)),
            end: endOfMonth(new Date(year, 11, 1)),
          };
        default:
          return { start: startOfMonth(now), end: endOfMonth(now) };
      }
    },
    []
  );

  useEffect(() => {
    const { start, end } = resolveRange(selectedInterval, selectedMonth, selectedYear);
    setStartDate(start);
    setEndDate(end);
  }, [selectedInterval, selectedMonth, selectedYear, resolveRange]);

  const fromIso = useMemo(
    () => (startDate ? startDate.toISOString() : undefined),
    [startDate]
  );
  const toIso = useMemo(() => (endDate ? endDate.toISOString() : undefined), [endDate]);

  return {
    startDate,
    endDate,
    fromIso,
    toIso,
    selectedInterval,
    setSelectedInterval,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    handleTimeIntervalChange: useCallback((start: Date, end: Date) => {
      setStartDate(start);
      setEndDate(end);
    }, []),
  };
}
